import { CartItem } from '~Types';
import { CartChild, CartLine } from './types';

/** Upsells grouped by the parent cart id they hang on. */
const groupChildrenByParent = (items: CartItem[]) => {
  const childrenOf = new Map<string, CartItem[]>();
  items.forEach((item) => {
    if (item.parentId) {
      childrenOf.set(item.parentId, [...(childrenOf.get(item.parentId) ?? []), item]);
    }
  });
  return childrenOf;
};

/** Same item, same variations, same add-ons, same comment -> same key -> same line. */
const lineKey = (parent: CartItem, children: CartItem[]) => {
  const variationIds = parent.variations.map((v) => v.id).join(',');
  const childIds = children
    .map((child) => child.itemId)
    .sort()
    .join('+');
  return `${parent.itemId}:${variationIds}|${childIds}|${parent.message ?? ''}`;
};

/** Add-ons collapsed by item: "2 x Dip" instead of two rows. */
const groupChildren = (children: CartItem[]) => {
  const grouped: CartChild[] = [];
  children.forEach((child) => {
    const paid = child.amount - child.discount;
    const existing = grouped.find((c) => c.itemId === child.itemId);
    if (existing) {
      existing.quantity += 1;
      existing.amount += paid;
    } else {
      grouped.push({ itemId: child.itemId, title: child.title, quantity: 1, amount: paid });
    }
  });
  return grouped;
};

/** One parent with its add-ons as a line of quantity 1. */
const toLine = (
  parent: CartItem,
  children: CartItem[],
  fallbackDiscountTitle: string,
): CartLine => {
  const originalAmount = parent.amount + children.reduce((sum, c) => sum + c.amount, 0);
  const amount =
    originalAmount - parent.discount - children.reduce((sum, c) => sum + c.discount, 0);
  const discountTitle =
    originalAmount > amount
      ? parent.discountTitle ||
        children.find((child) => child.discountTitle)?.discountTitle ||
        fallbackDiscountTitle
      : '';

  return {
    key: lineKey(parent, children),
    itemId: parent.itemId,
    title: parent.title,
    variationNames: parent.variations.map((v) => v.name).join(', '),
    variations: parent.variations,
    removedVariations: parent.removedVariations,
    comment: parent.message ?? '',
    quantity: 1,
    amount,
    originalAmount,
    discountTitle,
    imageUrl: parent.imageUrl ?? '',
    ageRestricted: !!parent.ageRestricted,
    ids: [parent.id],
    children: groupChildren(children),
  };
};

/** Folds a quantity-1 line into an existing line with the same key. */
const mergeInto = (line: CartLine, addition: CartLine) => {
  line.quantity += addition.quantity;
  line.amount += addition.amount;
  line.originalAmount += addition.originalAmount;
  line.ids.push(...addition.ids);
  addition.children.forEach((child) => {
    const existing = line.children.find((c) => c.itemId === child.itemId);
    if (existing) {
      existing.quantity += child.quantity;
      existing.amount += child.amount;
    } else {
      line.children.push({ ...child });
    }
  });
};

/** Everything CartLine draws - lines with the same fingerprint look identical. */
const fingerprint = (line: CartLine) =>
  [
    line.quantity,
    line.amount,
    line.originalAmount,
    line.discountTitle,
    line.variationNames,
    line.comment,
    line.imageUrl,
    line.ageRestricted,
    line.ids.join(','),
    line.children.map((c) => `${c.itemId}:${c.quantity}:${c.amount}`).join('|'),
  ].join('~');

/** Reuses the previous object for every line that did not change, so its CartLine skips its render. */
export const keepUnchangedLines = (lines: CartLine[], previous: Map<string, CartLine>) =>
  lines.map((line) => {
    const old = previous.get(line.key);
    return old && fingerprint(old) === fingerprint(line) ? old : line;
  });

/**
 * Turns the packaged cart into the grouped lines the cart shows. Upsells are
 * folded into their parent, and identical bundles collapse into one line.
 */
export const buildCartLines = (packagedItems: CartItem[], fallbackDiscountTitle: string) => {
  const childrenOf = groupChildrenByParent(packagedItems);
  const lines = new Map<string, CartLine>();

  packagedItems
    .filter((item) => !item.parentId)
    .forEach((parent) => {
      const line = toLine(parent, childrenOf.get(parent.id) ?? [], fallbackDiscountTitle);
      const existing = lines.get(line.key);
      if (existing) {
        mergeInto(existing, line);
      } else {
        lines.set(line.key, line);
      }
    });

  return Array.from(lines.values());
};
